'use client'

import React, { useCallback } from 'react'
import { useParams } from 'next/navigation'
import { Loader2 } from 'lucide-react'
import { getReceipt } from '@/lib/actions/order/get-reciept'
import { getPrinterType } from '@/lib/actions/register/get-printer-type'
import { useCheckout, useModals, useSearchParams, useSelectedCustomer } from '@/lib/hooks'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui'
import { PrintReceipt } from '@/components/molecules'

interface PrintReceiptModalProps extends React.ComponentPropsWithoutRef<'div'> {
  open?: boolean
}

export const PrintReceiptModal = ({ open }: PrintReceiptModalProps) => {
  const { closeModal } = useModals()
  const [isPending, startTransition] = React.useTransition()
  const [printerType, setPrinterType] = React.useState<string | null>('thermal')
  const [receipt, setReceipt] = React.useState<any>(null)
  const { orderId: orderIdParam } = useSearchParams()
  const { order_id } = useParams()
  const { setCustomer, setContinueAsGuest } = useSelectedCustomer()
  const { setPaymentMethod, setPayments, setCurrentStep } = useCheckout()

  const orderId = orderIdParam ?? order_id

  React.useEffect(() => {
    if (!orderId) return

    startTransition(async () => {
      const getPrinter = await getPrinterType()
      const receipt = await getReceipt(orderId)
      setPrinterType(getPrinter?.printerType ?? null)
      setReceipt(receipt)
    })
  }, [orderId])

  const handleClose = useCallback(() => {
    closeModal('PRINT_RECEIPT')
    setCurrentStep('ADD_PRODUCTS')
    setCustomer(null)
    setContinueAsGuest(false)
    setPaymentMethod(null)
    setPayments(null)
  }, [closeModal, setCustomer, setPaymentMethod, setContinueAsGuest, setCurrentStep, setPayments])

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          handleClose()
        }
      }}
    >
      <DialogContent
        closeOnOverlay
        onClose={handleClose}
        size="lg"
        className="min-h-[410px] gap-6"
      >
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-2">Receipt</div>
          </DialogTitle>
          <DialogDescription className="text-left">
            Receipt for order #{orderId}
          </DialogDescription>
        </DialogHeader>

        {isPending && (
          <div className="flex h-full w-full items-center justify-center">
            <Loader2 className="animate-spin text-gray-300" />
          </div>
        )}

        {orderId && !isPending && (
          <PrintReceipt printerType={printerType} receipt={receipt} />
        )}
      </DialogContent>
    </Dialog>
  )
}
